/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  message,
  DatePicker,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import {
  getTiposDeEmpleadosPresupuestales,
  postTipoDeEmpleadoPresupuestal,
  deleteTipoDeEmpleadoPresupuestal,
  patchTipoDeEmpleadoPresupuestal,
  getTipoDeEmpleadoPresupuestal,
  autorizarTipoDeEmpleadoPresupuestal,
} from 'api/catalogos/tiposDeEmpleadosPresupuestales';
import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';

export const tipodeempleadopresupuestalPermission = {
  permissionModel: 'tipodeempleadopresupuestal',
  catalogsModels: [],
  tabsModels: [],
};

const TipoDeEmpleadoPresupuestal = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [visibleConfirmInactivo, setVisibleConfirmInactivo] = useState(false);
  const [disableFields, setDisableFields] = useState(false);

  const formInstances = [
    form,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getTiposDeEmpleadosPresupuestales();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getTiposDeEmpleadosPresupuestales(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    if (!([].includes(key))) {
      const match = responseData || data.find((item) => item.id === key);
      const _detalle = await getTipoDeEmpleadoPresupuestal(match.id);
      setTimeout(() => {
        if (_detalle) {
          form.resetFields();
          // Convertimos la fecha string a objeto Moment para el DatePicker
          const formattedValues = {
            ...formatReceived(_detalle),
            vigencia_final: _detalle.vigencia_final ? moment(_detalle.vigencia_final) : null,
          };

          setTimeout(() => {
            form.setFieldsValue(formattedValues);
            setDisableFields(_detalle?.estados_globales !== ESTATUS_ACTIVO);
            setVisible(true);
          });
        }
      });
    } else {
      message.warn('Este campo no es editable');
    }
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();

      // Formateamos la fecha para enviar al backend (YYYY-MM-DD)
      if (values.vigencia_final) {
        values.vigencia_final = values.vigencia_final.format('YYYY-MM-DD');
      } else {
        values.vigencia_final = null;
      }

      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchTipoDeEmpleadoPresupuestal(key, values) : postTipoDeEmpleadoPresupuestal(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteTipoDeEmpleadoPresupuestal(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    form.resetFields();
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    const [key] = selectedRowKeys;
    if (key) {
      setVisibleAlert(true);
    }
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar un registro ya autorizado');
        return;
      }
      const saved = await autorizarTipoDeEmpleadoPresupuestal(key);
      if (saved) {
        const _data = await getTiposDeEmpleadosPresupuestales();
        setData(formatReceived(_data));
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 150,
    },
    {
      titleText: 'Concepto',
      dataIndex: 'descripcion', // En BD es descripcion, en UI mostramos como Concepto
      key: 'descripcion',
      width: 350,
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigencia_final',
      key: 'vigencia_final',
      width: 200,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : ''),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          customActions={[
            {
              onClick: () => setVisibleAutorizar(true),
              permission: { model: 'tipodeempleadopresupuestal', action: 'autorizar' },
              icon: <CheckCircleOutlined />,
              text: 'Autorizar',
              disabledBySelection: true,
              disabled: selectedData?.estados_globales !== ESTATUS_ACTIVO,
              className: 'success',
            },
          ]}
          disableDelete={selectedData?.estados_globales === ESTATUS_AUTORIZADO}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Tipo de Empleado Presupuestal`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableFields}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
            initialValues={{
              estados_globales: ESTATUS_ACTIVO,
            }}
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="clave"
                rules={rules.required}
                label="Clave"
                hasFeedback
              >
                <Input
                  allowClear
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="descripcion"
                rules={rules.required}
                label="Concepto"
                hasFeedback
              >
                <Input
                  allowClear
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="vigencia_final"
                label="Vigencia Final"
                hasFeedback
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estados}
                  activeInactive={selectedData?.estados_globales === ESTATUS_ACTIVO}
                  disabled={selectedRowKeys.length <= 0 || disableFields}
                  onChange={(val) => setVisibleConfirmInactivo(val === ESTATUS_INACTIVO)}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Tipo de Empleado ${form.getFieldValue('descripcion')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`Autorizar el Tipo de Empleado ${selectedData?.descripcion}`}
          open={visibleAutorizar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => setVisibleConfirmInactivo(false)}
          ConfirmInactive
          onCancel={() => {
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
            setVisibleConfirmInactivo(false);
          }}
          open={visibleConfirmInactivo}
          contentTitle={`Inactivar el Tipo de Empleado ${selectedData?.descripcion}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default TipoDeEmpleadoPresupuestal;
